import { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  Checkbox,
  FormControl,
  FormControlLabel,
  TextField,
} from "@mui/material";
import localize from "../../../common/localize";
import Combobox from "../../../components/common/Combobox";
import { PopperFullWidth, getInputLabelProps } from "../../../common/appStyles";
import {
  getOperationalStatusNameFromId,
  getValueInCurrency,
  MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION,
  OperationalStatusEnum,
} from "../../../common/utils";
import useGlobalContext from "../../../hooks/useGlobalContext";
import { getCurrencyFromSetup } from "../../../common/EntitiesManagement";
import PageTitleSubtitle from "../../../components/PageTitleSubTitle";

export default function FinishEventForm({ finishReview, setFinishReview }) {
  const {
    PERSONAL_API,
    FINISH_CONCEPT_ENDPOINT,
    config,
    getHeadersWithCorrespondingPagination,
  } = useGlobalContext();
  const maximumCharactersForDescription =
    MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION;
  const [finishObservationCount, setFinishObservationCount] = useState(0);
  const [personalData, setPersonalData] = useState(null);
  const [finishConceptOptions, setFinishConceptOptions] = useState([]);

  const getFinishConcepts = async () => {
    try {
      const result = await axios.get(
        FINISH_CONCEPT_ENDPOINT,
        getHeadersWithCorrespondingPagination(config, -1),
      );

      if (result.data?.ok || result.data?.Ok) {
        setFinishConceptOptions(result.data?.Data);
      }
    } catch (Exception) {
      console.error("Error getting finish concepts data: " + Exception.message);
    }
  };

  useEffect(() => {
    getFinishConcepts();
  }, []);

  const getAvailableOperationalStatuses = () => {
    if (finishReview.OperationalStatusId === OperationalStatusEnum.Canceled) {
      return [
        {
          Id: OperationalStatusEnum.Canceled,
          Name: getOperationalStatusNameFromId(OperationalStatusEnum.Canceled),
        },
      ];
    }

    return [
      {
        Id: OperationalStatusEnum.PendingReview,
        Name: getOperationalStatusNameFromId(
          OperationalStatusEnum.PendingReview,
        ),
      },
      {
        Id: OperationalStatusEnum.Finished,
        Name: getOperationalStatusNameFromId(OperationalStatusEnum.Finished),
      },
      {
        Id: OperationalStatusEnum.NotFinished,
        Name: getOperationalStatusNameFromId(OperationalStatusEnum.NotFinished),
      },
    ];
  };

  const getPersonal = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${PERSONAL_API}/`, requestConfig);

    setPersonalData(data?.data);
  };

  const handleFinishConceptChange = (e) => {
    setFinishReview({
      ...finishReview,
      FinishConceptId: !e ? null : e.Id,
      FinishConcept: e,
    });
  };

  const handleOperationalStatusChange = (e) => {
    setFinishReview({
      ...finishReview,
      OperationalStatusId: !e ? null : e.Id,
      OperationalStatusSelected: e,
      CanEdit: e?.Id === OperationalStatusEnum.PendingReview ? 1 : finishReview.CanEdit,
    });
  };

  const handleObservationChange = (e) => {
    setFinishReview({
      ...finishReview,
      Observation: e.target.value,
    });
  };

  const handlePersonalChange = (newPersonal) => {
    setFinishReview({
      ...finishReview,
      PersonalAssigned: newPersonal,
      PersonalId: !newPersonal ? 0 : newPersonal.id,
    });
  };

  const handleCanEditChange = (checkbox) => {
    setFinishReview({
      ...finishReview,
      CanEdit: checkbox.target.checked ? 1 : 0,
    });
  };

  useEffect(() => {
    getPersonal();
  }, []);

  useEffect(() => {
    if (finishReview?.Observation) {
      setFinishObservationCount(finishReview?.Observation.length);
    }
  }, [finishReview?.Observation]);

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div className="mt-2">
        <Combobox
          isRequired
          disabled={
            finishReview.OperationalStatusId === OperationalStatusEnum.Canceled
          }
          fieldValue={finishReview.OperationalStatusSelected}
          fieldLabel={localize.operational_status}
          handleValueChange={handleOperationalStatusChange}
          fieldOptions={getAvailableOperationalStatuses()}
        />
      </div>

      {finishReview.OperationalStatusId > OperationalStatusEnum.Canceled && (
        <div className="mt-4 flex flex-col gap-4">
          {finishConceptOptions?.length > 0 && (<Combobox
            disabled={
              !finishConceptOptions || finishConceptOptions?.length <= 0
            }
            fieldValue={finishReview.FinishConcept}
            fieldLabel={localize.finish_concept}
            handleValueChange={handleFinishConceptChange}
            fieldOptions={finishConceptOptions ?? []}
          />)}

          {finishReview?.FinishConcept?.ReplaceServiceValue && (
            <Alert severity="warning">
              <label className="text-xs">
                {`${localize.warning_service_value_replacement} `}
                <b>
                  {getValueInCurrency(
                    getCurrencyFromSetup(),
                    finishReview.FinishConcept.Value,
                  )}
                </b>
              </label>
            </Alert>
          )}
        </div>
      )}

      {finishReview.OperationalStatusId > OperationalStatusEnum.Canceled && (
        <div className="pt-4">
          <Combobox
            id="PersonalAssigned"
            fieldLabel={localize.assigned_to}
            fieldOptions={personalData?.Data ?? []}
            fieldValue={finishReview?.PersonalAssigned}
            customOptionLabel={(option) =>
              `${option.Identification} - ${option.SurName} ${option.LastName}`
            }
            handleValueChange={handlePersonalChange}
            popperFullWidth={PopperFullWidth}
          />
        </div>
      )}

      {finishReview.OperationalStatusId > OperationalStatusEnum.Canceled && (
        <div className="pt-5">
          <FormControl required fullWidth>
            <TextField
              InputLabelProps={getInputLabelProps()}
              inputProps={{
                style: {
                  fontSize: "0.8em",
                },
                maxLength: maximumCharactersForDescription,
              }}
              size="small"
              fullWidth
              multiline
              rows={5}
              id="outlined-basic"
              name="FinishObservationText"
              label={localize.conclusions}
              variant="outlined"
              onChange={handleObservationChange}
              value={finishReview?.Observation ?? ""}
              helperText={`${finishObservationCount}/${maximumCharactersForDescription}`}
            />
          </FormControl>
        </div>
      )}

      {finishReview.OperationalStatusId > OperationalStatusEnum.Canceled && 
      finishReview.OperationalStatusId !== OperationalStatusEnum.PendingReview && (
        <div className="pt-2">
          <div>
            <FormControlLabel
              size="small"
              name="CanEdit"
              disabled={
                finishReview.OperationalStatusId ===
                OperationalStatusEnum.PendingReview
              }
              control={
                <Checkbox
                  size="small"
                  name="CanEdit"
                  checked={finishReview.CanEdit === 1}
                  onChange={handleCanEditChange}
                />
              }
              label={
                <span style={{ fontSize: "0.8em" }}>
                  <PageTitleSubtitle
                    title={`${
                      finishReview.CanEdit === 1
                        ? localize.editable
                        : localize.non_editable
                    } (${localize.click_to_change})`}
                    subtitle={
                      finishReview.CanEdit === 1
                        ? localize.can_edit
                        : localize.finish_review_warning
                    }
                    useEventTitleStyle
                    useFieldTitleStyle
                  />
                </span>
              }
            />
          </div>
        </div>
      )}
    </div>
  );
}
